import React, { memo } from "react";
import { View, Pressable, ViewStyle } from "react-native";
import { Icon } from "@src/components/Icon";
import { useUnistyles } from "react-native-unistyles";
import { FormInput } from "../../FormInput";
import type { PickerFormatter, PickerIcon } from "../types";

interface PickerInputProps<TValue> {
  /** Current value to display */
  value: TValue | undefined;
  /** Formatter function to convert value to display string */
  formatter: PickerFormatter<TValue>;
  /** Callback when input is pressed */
  onPress: () => void;
  /** Icon to display on the right side */
  icon: PickerIcon;
  /** Error message to display */
  error?: string;
  /** Size variant */
  size?: "small" | "medium" | "large";
  /** Placeholder text */
  placeholder?: string;
  /** Text color */
  textColor?: string;
  /** Padding left */
  textInputPaddingLeft?: number;
  /** Container form input style */
  containerFormInputStyle?: ViewStyle;
  /** Show icon */
  showIcon?: boolean;
}

const ICON_MAP: Record<PickerIcon, "calendarOutline" | "clockOutline"> = {
  calendar: "calendarOutline",
  time: "clockOutline",
};

/**
 * Declarative input component for date/time pickers.
 * Renders a read-only FormInput with an icon that triggers picker modal.
 *
 * @example
 * ```tsx
 * <PickerInput
 *   value={timeValue}
 *   formatter={(v) => formatTimeValue(v, true)}
 *   onPress={show}
 *   icon="time"
 *   error={error}
 * />
 * ```
 */
function PickerInputInner<TValue>({
  value,
  formatter,
  onPress,
  icon,
  error,
  size = "medium",
  placeholder,
  textColor,
  textInputPaddingLeft,
  containerFormInputStyle,
  showIcon = true,
}: PickerInputProps<TValue>) {
  const { theme } = useUnistyles();
  const iconName = ICON_MAP[icon];
  const displayValue = formatter(value);

  return (
    <Pressable onPress={onPress}>
      <View style={{ pointerEvents: "none" }}>
        <FormInput
          placeholder={placeholder ?? (icon === "calendar" ? "date" : "time")}
          onChangeText={() => {}} // Read-only, handled by picker
          value={displayValue}
          error={error}
          iconRight={showIcon ? <Icon name={iconName} size={18} color={theme.colors.textMuted} /> : undefined}
          size={size}
          textInputColor={textColor}
          textInputPaddingLeft={textInputPaddingLeft}
          containerStyle={containerFormInputStyle}
        />
      </View>
    </Pressable>
  );
}

// Memoize with generic support
export const PickerInput = memo(PickerInputInner) as typeof PickerInputInner;
